import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  CircularProgress,
  Alert,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Tabs,
  Tab,
  Button,
  Chip
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Crop as CropIcon, TextFields as TextFieldsIcon } from '@mui/icons-material';
import ImageUploader from '../components/common/ImageUploader';
import ImageGrid from '../components/common/ImageGrid';
import { useImages } from '../context/images/ImagesContext';
import { useParams } from 'react-router-dom';
import { API_URL } from '../services/api/config';
import ProcessingModal from '../components/common/ProcessingModal';
import { toast } from 'react-toastify';
import { hybridUploadService } from '../services/upload/hybridUploadService';
import runpodService from '../services/runpod/runpodService';
import processingService from '../services/processing/processingService';

const RawDatasetPage = () => {
  const { id } = useParams();
  const { rawImages, loading, error, selectedInfluencer, fetchImagesByFolder, deleteImage, refreshCroppedImages } = useImages();
  // Using direct processingService import (now with RunPod integration)
  const [page, setPage] = useState(1);
  const [imagesPerPage, setImagesPerPage] = useState(48);
  const [filteredImages, setFilteredImages] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [sourceTab, setSourceTab] = useState(0); // 0 for All, 1 for OnlyFans, 2 for Twitter
  
  // Cropping state
  const [selectedForCrop, setSelectedForCrop] = useState(new Set());
  const [cropMode, setCropMode] = useState(false);
  const [sexPrompt, setSexPrompt] = useState('female');
  
  // Processing modal state
  const [processingModal, setProcessingModal] = useState({
    open: false,
    title: '',
    status: '',
    progress: null,
    error: null
  });

  // Fetch raw images when component mounts or selectedInfluencer changes
  useEffect(() => {
    if (selectedInfluencer && selectedInfluencer.id) {
      // Only fetch if we don't have data or if the influencer changed
      fetchImagesByFolder('raw', selectedInfluencer.id);
    }
  }, [selectedInfluencer?.id, fetchImagesByFolder]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setSourceTab(newValue);
    setPage(1); // Reset to first page when changing tabs
  };

  // Filter and paginate images
  useEffect(() => {
    if (rawImages.length > 0) {
      console.log('RawDatasetPage: rawImages:', rawImages.length, 'images');
      console.log('RawDatasetPage: selectedInfluencer:', selectedInfluencer);
      console.log('RawDatasetPage: First image influencerId:', rawImages[0]?.influencerId, 'influencer_id:', rawImages[0]?.influencer_id);

      // Filter by selected influencer since API might return wrong influencer's images
      let filtered = selectedInfluencer
        ? rawImages.filter(img => {
            const imgInfluencerId = img.influencerId || img.influencer_id;
            return imgInfluencerId === selectedInfluencer.id;
          })
        : rawImages;

      console.log('RawDatasetPage: After influencer filter:', filtered.length, 'images');
      
      // Filter by source (OnlyFans or Twitter)
      if (sourceTab === 1) { // OnlyFans
        filtered = filtered.filter(img => img.metadata?.source === 'onlyfans' || img.s3Key?.includes('onlyfans'));
      } else if (sourceTab === 2) { // Twitter
        filtered = filtered.filter(img => img.metadata?.source === 'twitter' || img.s3Key?.includes('twitter'));
      }
      
      setFilteredImages(filtered);
      setTotalPages(Math.ceil(filtered.length / imagesPerPage));
      
      // Reset to page 1 if current page is out of bounds
      if (page > Math.ceil(filtered.length / imagesPerPage)) {
        setPage(1);
      }
    } else {
      setFilteredImages([]);
      setTotalPages(1);
      setPage(1);
    }
  }, [rawImages, selectedInfluencer, imagesPerPage, page, sourceTab]);

  const handlePageChange = (event, value) => {
    setPage(value);
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleImagesPerPageChange = (event) => {
    setImagesPerPage(event.target.value);
    setPage(1); // Reset to first page when changing items per page
  };

  const handleSingleImageCrop = async (image) => {
    // Crop and caption a single image
    setProcessingModal({
      open: true,
      title: 'Cropping image...',
      status: 'submitting',
      progress: { total: 1, completed: 0 },
      error: null
    });
    
    try {
      const response = await processingService.cropImages(
        selectedInfluencer.id,
        [image],
        sexPrompt,
        (progress) => {
          setProcessingModal(prev => ({
            ...prev,
            status: progress.status || 'processing',
            progress: {
              total: progress.total,
              completed: progress.completed || 0,
              message: progress.message || 'Processing...'
            }
          }));
        }
      );
      
      if (response.jobId) {
        setProcessingModal(prev => ({
          ...prev,
          status: 'processing',
          jobId: response.jobId,
          progress: { ...prev.progress, message: 'Processing image...' }
        }));
        
        // Poll for completion
        await processingService.pollJobStatus(
          response.jobId,
          (status) => {
            setProcessingModal(prev => ({
              ...prev,
              progress: {
                total: 1,
                completed: status.completed || 0,
                message: status.message || 'Processing...'
              }
            }));
          }
        );
        
        // Note: Cropped image is created directly in S3 by RunPod
        // The backend needs to be updated to scan and register these images
        
        toast.success('Image cropped and captioned successfully!');
        setProcessingModal(prev => ({ ...prev, open: false }));
        // Refresh images after successful cropping
        setTimeout(() => {
          fetchImagesByFolder('raw', selectedInfluencer.id);
        }, 2000);
      } else {
        toast.success('Image cropped successfully!');
        setProcessingModal(prev => ({ ...prev, open: false }));
        // Refresh images after successful cropping
        setTimeout(() => {
          fetchImagesByFolder('raw', selectedInfluencer.id);
        }, 2000);
      }
    } catch (error) {
      console.error('Error cropping image:', error);
      setProcessingModal(prev => ({
        ...prev,
        status: 'error',
        error: error.message || 'Failed to crop image'
      }));
      toast.error('Failed to crop image');
      setTimeout(() => {
        setProcessingModal(prev => ({ ...prev, open: false }));
      }, 3000);
    }
  };

  const handleSingleImageCaption = async (image) => {
    // Caption only a single image - use processingService which handles S3 copy server-side
    setProcessingModal({
      open: true,
      title: 'Processing image for captioning...',
      status: 'submitting',
      progress: { total: 1, completed: 0 },
      error: null
    });
    
    try {
      // Use processingService which will handle the copy server-side
      const response = await processingService.captionImages(
        selectedInfluencer.id,
        [image],
        'raw', // Caption from raw folder (service will copy to cropped)
        (progress) => {
          setProcessingModal(prev => ({
            ...prev,
            status: progress.status || 'processing',
            progress: {
              total: 1,
              completed: progress.completed || 0,
              message: progress.message || 'Processing...'
            }
          }));
        }
      );

      setProcessingModal(prev => ({
        ...prev,
        status: 'completed'
      }));
      
      toast.success('Image captioned successfully!');
      
      // Refresh to show the new image and caption
      setTimeout(async () => {
        await fetchImagesByFolder('raw', selectedInfluencer.id);
        setProcessingModal(prev => ({ ...prev, open: false }));
      }, 2000);
      
    } catch (error) {
      console.error('Error captioning image:', error);
      setProcessingModal(prev => ({
        ...prev,
        status: 'error',
        error: error.message || 'Failed to caption image'
      }));
      toast.error('Failed to caption image');
      setTimeout(() => {
        setProcessingModal(prev => ({ ...prev, open: false }));
      }, 3000);
    }
  };

  const handleDeleteImage = async (imageId) => {
    if (window.confirm('Are you sure you want to delete this image? This action cannot be undone.')) {
      await deleteImage(imageId, 'raw');
    }
  };

  // Handle caption selected images (without cropping)
  const handleCaptionSelected = async () => {
    if (selectedForCrop.size === 0) {
      toast.warning('Please select images to caption');
      return;
    }

    const imagesToCaption = filteredImages.filter(img => selectedForCrop.has(img.id));
    
    setProcessingModal({
      open: true,
      title: `Processing ${imagesToCaption.length} images for captioning...`,
      status: 'submitting',
      progress: { total: imagesToCaption.length, completed: 0 },
      error: null
    });

    try {
      // Use processingService which handles S3 operations server-side
      const response = await processingService.captionImages(
        selectedInfluencer.id,
        imagesToCaption,
        'raw', // Caption from raw folder (service will copy to cropped)
        (progress) => {
          setProcessingModal(prev => ({
            ...prev,
            status: progress.status || 'processing',
            progress: {
              total: progress.total,
              completed: progress.completed || 0,
              message: progress.message || 'Processing...'
            }
          }));
        }
      );

      setProcessingModal(prev => ({
        ...prev,
        status: 'completed'
      }));
      
      toast.success(`Successfully captioned ${imagesToCaption.length} images`);
      setSelectedForCrop(new Set());
      setCropMode(false);
      
      // Wait a bit then refresh to show the captions
      setTimeout(async () => {
        await fetchImagesByFolder('raw', selectedInfluencer.id);
      }, 2000);
      
    } catch (error) {
      console.error('Captioning failed:', error);
      setProcessingModal(prev => ({
        ...prev,
        status: 'failed',
        error: error.message || 'Failed to caption images'
      }));
      toast.error('Failed to caption images');
    }
  };

  // Handle crop selected images
  const handleCropSelected = async () => {
    if (selectedForCrop.size === 0) {
      toast.warning('Please select images to crop');
      return;
    }

    const imagesToCrop = filteredImages.filter(img => selectedForCrop.has(img.id));
    
    setProcessingModal({
      open: true,
      title: `Cropping ${imagesToCrop.length} images...`,
      status: 'submitting',
      progress: { total: imagesToCrop.length, completed: 0 },
      error: null
    });

    try {
      const response = await processingService.cropImages(
        selectedInfluencer.id,
        imagesToCrop,
        sexPrompt,
        (progress) => {
          setProcessingModal(prev => ({
            ...prev,
            status: progress.status || 'processing',
            progress: {
              total: progress.total,
              completed: progress.completed || 0,
              message: progress.message || 'Processing...'
            }
          }));
        }
      );

      if (response.jobId) {
        setProcessingModal(prev => ({
          ...prev,
          status: 'processing',
          progress: { ...prev.progress, message: 'Processing images...' }
        }));

        await processingService.pollJobStatus(
          response.jobId,
          (status) => {
            setProcessingModal(prev => ({
              ...prev,
              progress: {
                total: imagesToCrop.length,
                completed: status.completed || 0,
                message: status.message || 'Processing...'
              }
            }));
          }
        );
      }

      // Note: Cropped images are created directly in S3 by RunPod
      // The backend needs to be updated to scan and register these images
      // For now, we'll just refresh and the backend should pick them up eventually

      setProcessingModal(prev => ({
        ...prev,
        status: 'completed'
      }));
      
      toast.success(`Successfully cropped ${imagesToCrop.length} images`);
      setSelectedForCrop(new Set());
      setCropMode(false);
      await fetchImagesByFolder('raw', selectedInfluencer.id);
      
    } catch (error) {
      console.error('Cropping failed:', error);
      setProcessingModal(prev => ({
        ...prev,
        status: 'failed',
        error: error.message || 'Failed to crop images'
      }));
      toast.error('Failed to crop images');
    }
  };

  // Get current images for the page
  const getCurrentImages = () => {
    const startIndex = (page - 1) * imagesPerPage;
    const endIndex = startIndex + imagesPerPage;
    return filteredImages.slice(startIndex, endIndex);
  };

  return (
    <Container>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Raw Dataset
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          View and manage raw images from OnlyFans and Twitter. Select images to add to your manual selection.
        </Typography>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {!selectedInfluencer && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Please select an influencer from the sidebar to view their images.
        </Alert>
      )}
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="subtitle1">
            {filteredImages.length} images found
          </Typography>
          
          {selectedInfluencer && (
            <>
              <ImageUploader
                influencer={selectedInfluencer}
                influencerId={selectedInfluencer.id}
                folder="raw"
                onUploadComplete={(count) => {
                  if (count > 0) {
                    fetchImagesByFolder('raw', selectedInfluencer.id);
                  }
                }}
              />
              
              <Button
                variant={cropMode ? "contained" : "outlined"}
                size="small"
                onClick={() => {
                  setCropMode(!cropMode);
                  setSelectedForCrop(new Set());
                }}
                startIcon={<CropIcon />}
              >
                {cropMode ? 'Cancel' : 'Crop Images'}
              </Button>
              
              {cropMode && (
                <>
                  <FormControl size="small" sx={{ minWidth: 100 }}>
                    <Select
                      value={sexPrompt}
                      onChange={(e) => setSexPrompt(e.target.value)}
                      size="small"
                    >
                      <MenuItem value="female">Female</MenuItem>
                      <MenuItem value="male">Male</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <Button
                    size="small"
                    onClick={() => {
                      const allIds = getCurrentImages().map(img => img.id);
                      setSelectedForCrop(new Set(allIds));
                    }}
                  >
                    Select Page
                  </Button>
                  
                  {selectedForCrop.size > 0 && (
                    <>
                      <Chip
                        label={`${selectedForCrop.size} selected`}
                        color="primary"
                        size="small"
                        onDelete={() => setSelectedForCrop(new Set())}
                      />
                      <Button
                        variant="contained"
                        size="small"
                        color="primary"
                        onClick={handleCropSelected}
                        sx={{ mr: 1 }}
                      >
                        Crop & Caption
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        color="secondary"
                        onClick={handleCaptionSelected}
                      >
                        Caption Only
                      </Button>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </Box>
        
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel id="images-per-page-label">Images per page</InputLabel>
          <Select
            labelId="images-per-page-label"
            id="images-per-page"
            value={imagesPerPage}
            label="Images per page"
            onChange={handleImagesPerPageChange}
          >
            <MenuItem value={48}>48</MenuItem>
            <MenuItem value={100}>100</MenuItem>
            <MenuItem value={200}>200</MenuItem>
            <MenuItem value={500}>500</MenuItem>
          </Select>
        </FormControl>
      </Box>
      
      {/* Source tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={sourceTab} 
          onChange={handleTabChange}
          aria-label="image source tabs"
        >
          <Tab label="All Images" />
          <Tab label="OnlyFans" />
          <Tab label="Twitter" />
        </Tabs>
      </Box>
      
      {loading && filteredImages.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
          <CircularProgress />
        </Box>
      ) : filteredImages.length === 0 ? (
        <Alert severity="info">
          No raw images found. Upload images or select a different influencer.
        </Alert>
      ) : (
        <>
          <ImageGrid
            images={getCurrentImages().map(image => {
              // Debug logging for production
              if (!image.url) {
                console.error('RawDatasetPage: Image missing URL:', {
                  id: image.id,
                  filename: image.filename,
                  s3Key: image.s3Key,
                  url: image.url,
                  hasPresignedUrl: !!image.presigned_url,
                  fullImage: image
                });
              }

              return {
                id: image.id,
                url: image.url, // Backend provides presigned_url mapped to url
                caption: image.caption || '',
                metadata: image.metadata || {},
                selected: selectedForCrop.has(image.id),
                s3Key: image.s3Key,
                filename: image.filename
              };
            })}
            loading={loading}
            folder="raw"
            actionIcon={cropMode ? null : <CropIcon />}
            actionTooltip={cropMode ? null : "Crop & Caption"}
            actionLabel={cropMode ? null : "Crop & Caption"}
            onActionClick={cropMode ? null : (imageId) => {
              const image = filteredImages.find(img => img.id === imageId);
              if (image) {
                handleSingleImageCrop(image);
              }
            }}
            secondaryActionIcon={<DeleteIcon />}
            secondaryActionTooltip="Delete Image"
            secondaryActionLabel="Delete"
            onSecondaryActionClick={(imageId) => {
              handleDeleteImage(imageId);
            }}
            tertiaryActionIcon={cropMode ? null : <TextFieldsIcon />}
            tertiaryActionTooltip={cropMode ? null : "Caption Only"}
            tertiaryActionLabel={cropMode ? null : "Caption Only"}
            onTertiaryActionClick={cropMode ? null : (imageId) => {
              const image = filteredImages.find(img => img.id === imageId);
              if (image) {
                handleSingleImageCaption(image);
              }
            }}
            onImageClick={cropMode ? (imageId) => {
              const newSelected = new Set(selectedForCrop);
              if (newSelected.has(imageId)) {
                newSelected.delete(imageId);
              } else {
                newSelected.add(imageId);
              }
              setSelectedForCrop(newSelected);
            } : null}
          />
          
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
              <Pagination 
                count={totalPages} 
                page={page} 
                onChange={handlePageChange} 
                color="primary" 
                size="large"
                disabled={loading}
              />
            </Box>
          )}
        </>
      )}
      
      {/* Processing Modal */}
      <ProcessingModal
        open={processingModal.open}
        onClose={() => setProcessingModal(prev => ({ ...prev, open: false }))}
        title={processingModal.title}
        status={processingModal.status}
        progress={processingModal.progress}
        error={processingModal.error}
        canClose={processingModal.status === 'completed' || processingModal.status === 'failed'}
      />
    </Container>
  );
};

export default RawDatasetPage;
